// src/components/heatmaps/ResultsSection.jsx

import { useState, useEffect, useRef } from "react";
import styles from "../../css/heatmaps/ResultsSection.module.css";
import axiosInstance from "../../api/AxiosConfig";
import AnalysisViewer from "./AnalysisViewer";
import HistoryView from "./HistoryView";
import Pack from "../Pack";

const ResultsSection = ({ analysisData, onNewAnalysis }) => {
  const [viewMode, setViewMode] = useState("analysis");
  const [historyData, setHistoryData] = useState([]);
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [hasFetchedHistory, setHasFetchedHistory] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Ref na kořenový element sekce výsledků pro scrollování po dokončení analýzy
  const containerRef = useRef(null);

  const [progressData, setProgressData] = useState({
    percent: 0,
    loaded: 0,
    total: 0,
  });

  useEffect(() => {
    if (analysisData && analysisData.length > 0) {
      setViewMode("analysis");
      setHasFetchedHistory(false);

      // Po doručení výsledků posuň pohled na sekci s výsledky
      // Použijeme double rAF, aby se zajistilo, že obsah je vykreslen před scrollováním
      const scrollAfterPaint = () => {
        const el = containerRef.current;
        if (!el) return;
        const prefersReducedMotion = window.matchMedia(
          "(prefers-reduced-motion: reduce)"
        ).matches;
        try {
          el.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "start",
          });
        } catch (e) {
          // Fallback pro starší prohlížeče
          el.scrollIntoView(true);
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(scrollAfterPaint));
    }
  }, [analysisData]);

  // Internal loader used by History button and by forced refresh after AI analysis
  const loadHistory = async (force = false) => {
    if (hasFetchedHistory && !force) return;
    setIsHistoryLoading(true);
    setErrorMessage(null);
    setProgressData({ percent: 0, loaded: 0, total: 0 });

    try {
      const config = {
        onDownloadProgress: (progressEvent) => {
          const { loaded, total, lengthComputable } = progressEvent;
          setProgressData((prev) => {
            const computable = lengthComputable && total > 0;
            const percent = computable
              ? Math.round((loaded * 100) / total)
              : Math.min(prev.percent + 2, 95); // fallback indeterminate progress
            return {
              percent,
              loaded,
              total: total || 0,
            };
          });
        },
      };
      const response = await axiosInstance.get("heatmaps/", config);

      if (response.data && Array.isArray(response.data)) {
        // finalize progress
        setProgressData((prev) => ({ ...prev, percent: 100 }));
        setHistoryData(response.data);
        setHasFetchedHistory(true);
      } else {
        throw new Error("Neplatný formát dat z serveru");
      }
    } catch (error) {
      console.error("Chyba při načítání historie:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.message ||
        "Nepodařilo se načíst historii. Zkuste to prosím později.";
      setErrorMessage(errorMsg);
      setHistoryData([]);
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const handleHistoryClick = async () => {
    setViewMode("history");
    await loadHistory(false);
  };

  // Callback passed to children: after AI analysis, drop cache and reload if on History tab
  const handleAiAnalysisCompleted = () => {
    setHasFetchedHistory(false);
    if (viewMode === "history") {
      loadHistory(true);
    }
  };

  return (
    <Pack>
      <div ref={containerRef} className={`box ${styles.container}`}>
        <div className={styles.panel}>
          <div className={styles.header}>
            <div className={styles.viewModeToggle}>
              <span
                className={`${styles.activeBg} ${
                  viewMode === "history" ? styles.right : ""
                }`}
                aria-hidden="true"
              />
              <button
                onClick={() => setViewMode("analysis")}
                className={`${styles.toggleButton} ${
                  viewMode === "analysis" ? styles.active : ""
                }`}
              >
                Analýza
              </button>
              <button
                onClick={handleHistoryClick}
                className={`${styles.toggleButton} ${
                  viewMode === "history" ? styles.active : ""
                }`}
              >
                Historie
              </button>
            </div>
          </div>

          <div className={styles.content}>
            {viewMode === "analysis" ? (
              <AnalysisViewer
                analysisData={analysisData || []}
                onAiAnalysisCompleted={handleAiAnalysisCompleted}
              />
            ) : errorMessage ? (
              <div className={styles.errorMessage}>
                <h3>Chyba při načítání historie</h3>
                <p>{errorMessage}</p>
                <button
                  onClick={() => {
                    setHasFetchedHistory(false);
                    handleHistoryClick();
                  }}
                  className={styles.retryButton}
                >
                  Zkusit znovu
                </button>
              </div>
            ) : (
              <HistoryView
                historyData={historyData}
                isLoading={isHistoryLoading}
                progressData={progressData}
                onAiAnalysisCompleted={handleAiAnalysisCompleted}
              />
            )}
          </div>
        </div>
      </div>
    </Pack>
  );
};

export default ResultsSection;
